import {generateInitialState} from "@/helpers/generateInitialState";
import {appSettingsInitialState} from "@/features/initialStates";
import {createSlice, isAnyOf} from "@reduxjs/toolkit";
import {loadAllFormQuestions, loadAppSettings, loadSaasPricing, loadTestKitPricing} from "./appSettingsActions";

const initialState = await generateInitialState("appSettings", appSettingsInitialState);

export const appSettingsSlice = createSlice({
  name: "appSettings",
  initialState,
  reducers: {
    updateFormInitialValue: (state, action) => {
      const newState = {...state.data, ...action.payload};

      state.data = newState;
      state.isFetching = false;
      state.hasFailed = false;
      state.errorMsg = "";
    },
    togglePinScreen: (state, action) => {
      const newState = {...state.data, showPinScreen: action.payload};

      state.isFetching = false;
      state.data = newState;
      state.hasFailed = false;
      state.errorMsg = "";
    },
  },
  extraReducers: builder => {
    builder
      .addCase(loadAppSettings.fulfilled, (state, action) => {
        const newState = {...state.data, ...action.payload};

        state.data = newState;
        state.isFetching = false;
        state.hasFailed = false;
        state.errorMsg = "";
      })
      .addCase(loadAllFormQuestions.fulfilled, (state, action) => {
        const newState = {...state.data, formQuestions: action.payload};

        state.data = newState;
        state.isFetching = false;
        state.hasFailed = false;
        state.errorMsg = "";
      })
      .addCase(loadTestKitPricing.fulfilled, (state, action) => {
        const newState = {...state.data, testKitSettings: action.payload?.testKitSettings || {}};

        state.data = newState;
        state.isFetching = false;
        state.hasFailed = false;
        state.errorMsg = "";
      })
      .addCase(loadSaasPricing.fulfilled, (state, action) => {
        const newState = {...state.data, saasPrices: action.payload || {}};

        state.data = newState;
        state.isFetching = false;
        state.hasFailed = false;
        state.errorMsg = "";
      })
      .addMatcher(
        isAnyOf(
          loadAppSettings.pending,
          loadAllFormQuestions.pending,
          loadTestKitPricing.pending,
          loadSaasPricing.pending,
        ),
        (state, action) => {
          state.isFetching = true;
          state.hasFailed = false;
          state.errorMsg = "";
        },
      )
      .addMatcher(
        isAnyOf(
          loadAppSettings.rejected,
          loadAllFormQuestions.rejected,
          loadTestKitPricing.rejected,
          loadSaasPricing.rejected,
        ),
        (state, action) => {
          const {message = "Something went wrong"} = action.payload || {};

          state.isFetching = false;
          state.hasFailed = true;
          state.errorMsg = message;
        },
      );
  },
  selectors: {
    allDataSelector: state => state.data,
    formQuestionsSelector: state => state.data.formQuestions,
    testKitSettingsSelector: state => state.data.testKitSettings,
    showDelayMessageSelector: state => state.data.labServicesStatus?.showDelayMessage,
    partnerClinicsSelector: state => state.data.partnerClinics,
    showPinScreenSelector: state => state.data.showPinScreen,
    subscriptionsCancelOptionsSelector: state => state.data.subscriptionsCancelOptions,
    voucherStatusSelector: state => state.data.voucherStatus,
    bloodAmountSettingsSelector: state => state.data.bloodAmountSettings,
    deliverySettingsSelector: state => state.data.deliverySettings,
    phlebotomyOptionsSelector: state => state.data.phlebotomySettings,
    preBookSettingsSelector: state => state.data.preBook,
    referralCodeSettingsSelector: state => state.data.referralCode,
  },
});

export const {updateFormInitialValue, togglePinScreen} = appSettingsSlice.actions;
export const {
  allDataSelector,
  bloodAmountSettingsSelector,
  deliverySettingsSelector,
  formQuestionsSelector,
  partnerClinicsSelector,
  showDelayMessageSelector,
  showPinScreenSelector,
  subscriptionsCancelOptionsSelector,
  testKitSettingsSelector,
  voucherStatusSelector,
  phlebotomyOptionsSelector,
  preBookSettingsSelector,
  referralCodeSettingsSelector,
} = appSettingsSlice.selectors;
export default appSettingsSlice.reducer;
